import React, { useState, useEffect } from "react";
import DaysInput from "../../../UI/DaysInput/DaysInput";
import { DateRangePicker } from "react-nice-dates";
import { ru } from "date-fns/locale";
import AddCitySvg from "../../../adRole/ads/createAds/svg/AddCitySvg";
import DeleteCitySvg from "../../../adRole/ads/createAds/svg/DeleteCitySvg";

const validateTimeInterval = (intervals, index) => {
    const toMinutes = (t) => {
        const [h, m] = t.split(":").map(Number);
        return h * 60 + m;
    };

    const current = intervals[index];
    if (!current.start || !current.end) return true;

    const start = toMinutes(current.start);
    const end = toMinutes(current.end);

    if (start > end) return false;

    const sorted = intervals
        .map((x, i) => ({ ...x, _i: i }))
        .filter(x => x.start && x.end)
        .sort((a, b) => toMinutes(a.start) - toMinutes(b.start));

    const pos = sorted.findIndex(x => x._i === index);

    const prev = sorted[pos - 1];
    const next = sorted[pos + 1];

    if (prev && toMinutes(prev.end) > start) return false;
    if (next && end > toMinutes(next.start)) return false;

    return true;
};

const TimeSlotComponent = ({ slotIndex, interval, updateInterval, removeInterval, addInterval, error, isLast }) => {
    const handleChange = (type, event) => {
        updateInterval(slotIndex, type, event.target.value);
    };

    return (
        <>
            <div className="create_search_time_flex-inputs">
                <div className="create_search_ads_time">
                    <label>С</label>
                    <input
                        type="time"
                        className="input"
                        value={interval.start}
                        onChange={(e) => handleChange("start", e)}
                    />
                </div>

                <div className="create_search_ads_time">
                    <label>До</label>
                    <input
                        type="time"
                        className="input"
                        value={interval.end}
                        onChange={(e) => handleChange("end", e)}
                    />
                </div>

                {isLast ? (
                    <button type="button" onClick={addInterval}>
                        <AddCitySvg />
                    </button>
                ) : (
                    <button type="button" onClick={() => removeInterval(slotIndex)}>
                        <DeleteCitySvg />
                    </button>
                )}
            </div>

            {error && <div className="error">{error}</div>}
        </>
    );
};

const WorkingTimeRole = ({ initialData, onChange }) => {
    const [workSchedule, setWorkSchedule] = useState({
        days: {},
        activeDay: null,
        startDateException: null,
        endDateException: null,
        _initialized: false,
    });

    const [intervalErrors, setIntervalErrors] = useState({});

    useEffect(() => {
        if (initialData && !workSchedule._initialized) {
            const days = {};

            const capitalize = (str) =>
                str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

            Object.keys(initialData.days || {}).forEach((day) => {
                const normalized = capitalize(day);

                days[normalized] = (initialData.days[day] || [])
                    .filter(i => i.start && i.end)
                    .map(interval => ({
                        start: interval.start || "",
                        end: interval.end || "",
                    }));
            });

            setWorkSchedule({
                days,
                startDateException: initialData.startDateException
                    ? new Date(initialData.startDateException)
                    : null,
                endDateException: initialData.endDateException
                    ? new Date(initialData.endDateException)
                    : null,
                _initialized: true,
            });
        }
    }, [initialData, workSchedule._initialized]);

    useEffect(() => {
        if (onChange) {
            const { _initialized, activeDay, ...pure } = workSchedule;

            const filteredDays = {};
            Object.keys(pure.days).forEach(day => {
                const filledSlots = pure.days[day].filter(slot => slot.start && slot.end);
                if (filledSlots.length > 0) {
                    filteredDays[day] = filledSlots;
                }
            });

            onChange({
                ...pure,
                days: filteredDays
            });
        }
    }, [workSchedule, onChange]);

    const selectDay = (day) => {
        setWorkSchedule((prev) => {
            const existing = prev.days[day];

            return {
                ...prev,
                activeDay: day,
                days: {
                    ...prev.days,
                    [day]: existing && existing.length > 0
                        ? existing
                        : [{ start: "", end: "" }],
                },
            };
        });

        setIntervalErrors({});
    };


    const addInterval = () => {
        const day = workSchedule.activeDay;
        if (!day) return;

        setWorkSchedule((prev) => ({
            ...prev,
            days: {
                ...prev.days,
                [day]: [...prev.days[day], { start: "", end: "" }],
            },
        }));
    };

    const updateInterval = (index, type, value) => {
        const day = workSchedule.activeDay;
        if (!day) return;

        setWorkSchedule((prev) => {
            const updated = [...prev.days[day]];
            updated[index] = { ...updated[index], [type]: value };

            const isValid = validateTimeInterval(updated, index);

            setIntervalErrors((err) => ({
                ...err,
                [index]: isValid ? null : "Выбранный интервал пересекается с другим.",
            }));

            if (!isValid) return prev;

            return {
                ...prev,
                days: {
                    ...prev.days,
                    [day]: updated,
                },
            };
        });
    };

    const removeInterval = (index) => {
        const day = workSchedule.activeDay;
        if (!day) return;

        setIntervalErrors((prev) => {
            const clone = { ...prev };
            delete clone[index];
            return clone;
        });

        setWorkSchedule((prev) => {
            const updated = [...prev.days[day]];
            updated.splice(index, 1);

            return {
                ...prev,
                days: {
                    ...prev.days,
                    [day]: updated,
                },
            };
        });
    };

    const activeIntervals = workSchedule.activeDay
        ? workSchedule.days[workSchedule.activeDay]
        : [];

    return (
        <div className="admin__layout" style={{ marginTop: "20px" }}>
            <div className="create_ads_m_block time-role">
                <div className="advers___title">Рабочий график</div>

                <DaysInput
                    days={workSchedule.days}
                    activeDay={workSchedule.activeDay}
                    onSelectDay={selectDay}
                />

                {workSchedule.activeDay && (
                    <>
                        <h3 className="advers___title">Рабочее время</h3>

                        <div className="create-block">
                            <div className="create_search_time_flex">
                                {activeIntervals.map((interval, idx) => (
                                    <TimeSlotComponent
                                        key={idx}
                                        slotIndex={idx}
                                        interval={interval}
                                        updateInterval={updateInterval}
                                        removeInterval={removeInterval}
                                        addInterval={addInterval}
                                        error={intervalErrors[idx]}
                                        isLast={idx === activeIntervals.length - 1}
                                    />
                                ))}
                            </div>
                            <p>
                                <span>*</span> Графики влияют на рабочее время модераторов. Если
                                оставить время по умолчанию, то у модератора остается круглосуточный
                                доступ к админ-панели.
                            </p>
                        </div>
                    </>
                )}

                <h3 className="advers___title">Календарные дни-исключения</h3>

                <div className="create-block">
                    <div className="create_search_time_flex">
                        <DateRangePicker
                            startDate={workSchedule.startDateException}
                            endDate={workSchedule.endDateException}
                            onStartDateChange={(date) =>
                                setWorkSchedule((prev) => ({
                                    ...prev,
                                    startDateException: date,
                                }))
                            }
                            onEndDateChange={(date) =>
                                setWorkSchedule((prev) => ({
                                    ...prev,
                                    endDateException: date,
                                }))
                            }
                            locale={ru}
                        >
                            {({ startDateInputProps, endDateInputProps, focus }) => (
                                <div className="data-picker-flex">
                                    <input
                                        className={`input ${focus === "startDate" ? "-focused" : ""}`}
                                        {...startDateInputProps}
                                        placeholder="Дата начала"
                                    />

                                    <input
                                        className={`input ${focus === "endDate" ? "-focused" : ""}`}
                                        {...endDateInputProps}
                                        placeholder="Дата окончания"
                                    />
                                </div>
                            )}
                        </DateRangePicker>
                    </div>

                    <p>
                        <span>*</span> В указанные дни доступ будет закрыт.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default WorkingTimeRole;
